import styled from 'styled-components'
import { orderBy } from 'lodash'
import ReactCountryFlag from 'react-country-flag'
import { useSelector } from 'react-redux'

import { formatNumber } from '../../utils/numbers'
import { translate } from '../../utils/translation'
import { fonts, palette } from '../../styles/variables'
import { LoadingResponsive } from '../loading/Loading'
import noContent from '../../styles/assets/tableIcons/no_content.svg'
import { Text } from '../topography'
import plusOrange from '../../styles/assets/tableIcons/circle_plus.svg'
import plusGray from '../../styles/assets/tableIcons/circle_plus_grey.svg'
import plusOutline from '../../styles/assets/tableIcons/circle_plus_outline.svg'
import { getLocationInfo } from '../../utils/locations'
import { generateKey } from '../../utils/functions'
import { selectCachedLocations } from '../../state/selectors/locationManager'

import { NoDataIcon } from './styled'

const KeywordList = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  overflow: 'auto',
  height: props.height || 'auto',
  position: 'relative',
  minHeight: '200px',
  padding: '16px',
  background: palette.white,
  border: `1px solid ${palette.lightGray}`,
  boxSizing: 'border-box',
  borderRadius: '2px',
}))

const PlusIcon = styled.div((props) => ({
  width: '20px',
  minWidth: '20px',
  height: '20px',
  minHeight: '20px',
  background: `url(${props.disable ? plusGray : plusOrange})`,
  backgroundSize: 'contain',
}))

const KeywordBox = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 16px 8px 8px',
  width: '100%',
  background: props.disable ? palette.lightGray30 : palette.white,
  border: `1px solid ${palette.lightGray}`,
  boxSizing: 'border-box',
  borderRadius: '2px',
  margin: '8px 0px',
  cursor: 'pointer',
  pointerEvents: props.disable ? 'none' : 'all',
  ':hover': {
    background: palette.lightGray30,
    '.keywordbox-keyword-info': {
      color: props.disable ? palette.darkGray20 : palette.darkGray,
      '& b': {
        color: props.disable ? palette.darkGray20 : palette.darkGray,
      },
    },
    '.keywordbox-plus-icon': {
      background: `url(${props.disable ? plusGray : plusOutline})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    },
  },
  '.keywordbox-plus-icon': {
    background: `url(${props.disable ? plusGray : plusOrange})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  ':first-child': {
    marginTop: 0,
  },
  ':last-child': {
    marginBottom: 0,
  },
}))

const KeywordInfo = styled.div({})

const NoDataDiv = styled.div({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
})

const Title = styled.div({
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  marginBottom: 8,
})

const handleGBToUK = (code) => (code === 'GB' ? 'UK' : code)

export default ({
  availableKeywords,
  selectedKeywords,
  handleAddKeyword,
  width = '100%',
  height = '300px',
}) => {
  const cachedLocations = useSelector(selectCachedLocations())

  const renderKeywordList = () => {
    if (!availableKeywords) return <LoadingResponsive offsetY="15%" />
    if (availableKeywords.length === 0) {
      return (
        <NoDataDiv>
          <NoDataIcon src={noContent} />
          {translate('no_keywords_available')}
        </NoDataDiv>
      )
    }

    return orderBy(
      availableKeywords,
      ['volume', 'position'],
      ['desc', 'asc'],
    ).map((item) => {
      const exist = selectedKeywords.some(
        (addedKeyword) => addedKeyword === item.keyword,
      )
      return (
        <KeywordBox
          key={generateKey([item.loc_id, item.lang, item.keyword])}
          data-testid="keyword-box-item"
          onClick={() => handleAddKeyword(item.keyword)}
          disable={exist}
        >
          <KeywordInfo>
            <Text
              className="keywordbox-keyword"
              lineHeight="14px"
              fontFamily={fonts.primaryBook}
              color={exist ? palette.darkGray30 : palette.darkGray}
            >
              {item.keyword}
            </Text>
            <Text
              className="keywordbox-keyword-info"
              fontSize="12px"
              lineHeight="12px"
              color={exist ? palette.darkGray20 : palette.darkGray70}
              boldColor={exist ? palette.darkGray20 : palette.darkGray70}
              margin="6px 0 0 0"
              dangerouslySetInnerHTML={{
                __html: translate(
                  'your_position_volume_info',
                  Math.round(item.position),
                  formatNumber(item.volume),
                ),
              }}
            />
            {item.lang && item.loc_id && (
              <Text margin="8px 0 0 0">
                <ReactCountryFlag
                  countryCode={
                    getLocationInfo(item.loc_id, cachedLocations).countryIsoCode
                  }
                  svg
                  style={{
                    marginTop: '-3px',
                    marginRight: '6px',
                    width: '1.3333em',
                    height: '1em',
                  }}
                />
                {`${handleGBToUK(
                  getLocationInfo(item.loc_id, cachedLocations).countryIsoCode,
                )} - ${item.lang.toUpperCase()}`}
              </Text>
            )}
          </KeywordInfo>
          <PlusIcon className="keywordbox-plus-icon" />
        </KeywordBox>
      )
    })
  }

  return (
    <div style={{ width }}>
      <Title>{translate('kw_you_rank_label')}</Title>
      <KeywordList data-testid="keyword-box-list" height={height}>
        {renderKeywordList()}
      </KeywordList>
    </div>
  )
}
